import { useId, useMemo } from 'react'

// Lightweight SVG sparkline. No chart library; the math is small enough
// that adding a dep would cost more than it saves. Pass a numeric array
// and a width/height; the component normalises, renders the path, and
// optionally drops endpoint dots.
//
// Engagement use case: feed it the .likes (or .impressions, or
// .engagementRate) values from a post's snapshot history (1h → 30d) to
// see the decay curve at a glance.

const DEFAULT_STROKE = '#d4582d'
const DEFAULT_FILL = 'rgba(212, 88, 45, 0.12)'

interface Point {
  x: number
  y: number
}

function isNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

export function buildPath(
  values: ReadonlyArray<number | null | undefined>,
  width: number,
  height: number,
  padding: number
): string {
  if (!Array.isArray(values) || values.length < 2) return ''
  const numericValues = values.map((value) => (isNumber(value) ? value : 0))
  const max = Math.max(...numericValues)
  const min = Math.min(...numericValues)
  const range = max - min || 1
  const innerWidth = Math.max(1, width - padding * 2)
  const innerHeight = Math.max(1, height - padding * 2)
  const stepX = innerWidth / (numericValues.length - 1)
  return numericValues
    .map((value, index) => {
      const normalised = (value - min) / range
      const x = padding + index * stepX
      const y = padding + innerHeight * (1 - normalised)
      return `${index === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`
    })
    .join(' ')
}

export function buildArea(
  path: string,
  width: number,
  height: number,
  padding: number
): string {
  if (!path) return ''
  return `${path} L ${(width - padding).toFixed(2)} ${(
    height - padding
  ).toFixed(2)} L ${padding.toFixed(2)} ${(height - padding).toFixed(2)} Z`
}

export function buildEndpointDots(
  values: ReadonlyArray<number | null | undefined>,
  width: number,
  height: number,
  padding: number
): Point[] {
  if (!Array.isArray(values) || values.length < 2) return []
  const numericValues = values.map((value) => (isNumber(value) ? value : 0))
  const max = Math.max(...numericValues)
  const min = Math.min(...numericValues)
  const range = max - min || 1
  const innerWidth = Math.max(1, width - padding * 2)
  const innerHeight = Math.max(1, height - padding * 2)
  const lastIndex = numericValues.length - 1
  const stepX = innerWidth / lastIndex
  const project = (value: number, index: number): Point => ({
    x: padding + index * stepX,
    y: padding + innerHeight * (1 - (value - min) / range),
  })
  // We've early-returned if length < 2, so [0] and [lastIndex] are valid.
  return [
    project(numericValues[0]!, 0),
    project(numericValues[lastIndex]!, lastIndex),
  ]
}

export interface SparklineProps {
  values: ReadonlyArray<number | null | undefined>
  width?: number
  height?: number
  padding?: number
  stroke?: string
  fill?: string
  showEndpoints?: boolean
  ariaLabel?: string
}

export function Sparkline({
  values,
  width = 160,
  height = 40,
  padding = 4,
  stroke = DEFAULT_STROKE,
  fill = DEFAULT_FILL,
  showEndpoints = true,
  ariaLabel = 'sparkline',
}: SparklineProps) {
  const gradientId = useId()
  const path = useMemo(
    () => buildPath(values, width, height, padding),
    [values, width, height, padding]
  )
  const area = useMemo(
    () => buildArea(path, width, height, padding),
    [path, width, height, padding]
  )
  const endpoints = useMemo(
    () => buildEndpointDots(values, width, height, padding),
    [values, width, height, padding]
  )

  if (!path) {
    return (
      <svg
        width={width}
        height={height}
        role="img"
        aria-label={`${ariaLabel} (no data)`}
      >
        <rect
          x={padding}
          y={height / 2 - 0.5}
          width={width - padding * 2}
          height={1}
          fill="rgba(176, 184, 199, 0.2)"
        />
      </svg>
    )
  }

  // Wave 47x — premium polish. Three additions over the basic
  // stroke+fill: (1) a soft glow filter so the line reads as luminous
  // instead of pencil-drawn; (2) a subtle stroke gradient that fades
  // slightly into history to suggest "recent matters more"; (3) a
  // halo ring around the latest endpoint so the eye lands on the
  // "right now" data point.
  const glowId = `${gradientId}-glow`
  const strokeGradId = `${gradientId}-stroke`
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={ariaLabel}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={fill} stopOpacity="1" />
          <stop offset="100%" stopColor={fill} stopOpacity="0" />
        </linearGradient>
        <linearGradient id={strokeGradId} x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor={stroke} stopOpacity="0.55" />
          <stop offset="60%" stopColor={stroke} stopOpacity="0.85" />
          <stop offset="100%" stopColor={stroke} stopOpacity="1" />
        </linearGradient>
        <filter id={glowId} x="-20%" y="-50%" width="140%" height="200%">
          <feGaussianBlur stdDeviation="1.2" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <path
        className="studio-sparkline-area"
        d={area}
        fill={`url(#${gradientId})`}
        stroke="none"
      />
      <path
        className="studio-sparkline-line"
        d={path}
        fill="none"
        stroke={`url(#${strokeGradId})`}
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        filter={`url(#${glowId})`}
      />
      {showEndpoints
        ? endpoints.map((point, index) => {
            const isLatest = index === endpoints.length - 1
            return (
              <g key={index} className="studio-sparkline-endpoint">
                {isLatest ? (
                  <circle
                    cx={point.x}
                    cy={point.y}
                    r="5"
                    fill={stroke}
                    fillOpacity="0.18"
                  />
                ) : null}
                <circle
                  cx={point.x}
                  cy={point.y}
                  r={isLatest ? '2.6' : '2.0'}
                  fill={stroke}
                  fillOpacity={isLatest ? '1' : '0.5'}
                />
              </g>
            )
          })
        : null}
    </svg>
  )
}

export default Sparkline
